import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  getAvatarColor,
} from '@heidi/design-system/avatar';
import { DropdownMenuItem } from '@heidi/design-system/dropdown-menu';
import { TypographyP2 } from '@heidi/design-system/typography';
import { AssigneeType } from '@heidi/schema/ml-scribe';
import { Check } from 'lucide-react';

import {
  getInitialsFromFullName,
  getTeamInitial,
} from './clinical-task-assignee-list-items.utils';

interface AssigneeTeamItemProps {
  id: string;
  name: string;
  isSelected: boolean;
  onSelect: () => void;
}

export function AssigneeTeamItem({
  id,
  name,
  isSelected,
  onSelect,
}: AssigneeTeamItemProps) {
  return (
    <DropdownMenuItem onClick={onSelect}>
      <Avatar color={getAvatarColor(id)} size="xs" shape="square" className="shrink-0">
        <AvatarFallback>{getTeamInitial(name)}</AvatarFallback>
      </Avatar>
      <TypographyP2 className="flex-1 truncate text-start">{name}</TypographyP2>
      {isSelected && <Check className="text-text-primary" />}
    </DropdownMenuItem>
  );
}

interface AssigneeMemberItemProps {
  label: string;
  // Stable, non-localized color seed: `label` can be the translated "Assigned"
  // fallback, tying the color to locale and diverging from the trigger chip when
  // the name is null.
  colorSeed: string;
  avatarUrl?: string | null;
  initials: string;
  isSelected: boolean;
  onSelect: () => void;
}

export function AssigneeMemberItem({
  label,
  colorSeed,
  avatarUrl,
  initials,
  isSelected,
  onSelect,
}: AssigneeMemberItemProps) {
  return (
    <DropdownMenuItem onClick={onSelect}>
      <Avatar size="xs" color={getAvatarColor(colorSeed)} className="shrink-0">
        <AvatarImage src={avatarUrl ?? undefined} alt={label} />
        <AvatarFallback>{initials}</AvatarFallback>
      </Avatar>
      <TypographyP2 className="flex-1 truncate text-start">{label}</TypographyP2>
      {isSelected && <Check className="text-text-primary" />}
    </DropdownMenuItem>
  );
}

interface AssigneeCurrentItemProps {
  assigneeId?: string | null;
  assigneeName?: string | null;
  assigneeAvatarUrl?: string | null;
  assigneeType?: AssigneeType;
  assignedLabel: string;
  onSelect: () => void;
}

// Renders the current assignee from task props, not search results, so it shows on open without typing.
export function AssigneeCurrentItem({
  assigneeId,
  assigneeName,
  assigneeAvatarUrl,
  assigneeType,
  assignedLabel,
  onSelect,
}: AssigneeCurrentItemProps) {
  const label = assigneeName ?? assignedLabel;

  if (assigneeType === AssigneeType.team) {
    return (
      <AssigneeTeamItem
        id={assigneeId ?? ''}
        name={label}
        isSelected
        onSelect={onSelect}
      />
    );
  }

  return (
    <AssigneeMemberItem
      label={label}
      colorSeed={assigneeName ?? ''}
      avatarUrl={assigneeAvatarUrl}
      initials={getInitialsFromFullName(assigneeName)}
      isSelected
      onSelect={onSelect}
    />
  );
}
